import { useState } from "react";
import { SectionTitle } from "../_shared/section-title";
import { PageShell, Stack } from "../_shared/page-shell";
import { Card } from "../_shared/card";
import { useDemo } from "../_shared/demo.provider";
import { back } from "../_shared/router";
import { formatFrenchDate } from "../_shared/date-fmt";
import { IconClipboard } from "../_shared/icons";
import {
  appointmentReasons,
  availableSlots,
  children,
  type Child,
} from "../_data/mock";

function parseSlot(slotKey: string): { isoDate: string; time: string } | null {
  const [dayId, time] = slotKey.split("__");
  if (!dayId || !time) return null;
  return { isoDate: dayId.replace(/^d-/, ""), time };
}

function getRescheduleId(): string | null {
  if (typeof window === "undefined") return null;
  const hash = window.location.hash;
  const qIndex = hash.indexOf("?");
  if (qIndex < 0) return null;
  return new URLSearchParams(hash.slice(qIndex + 1)).get("reschedule");
}

const childPill = {
  orange:
    "bg-[var(--child-1-light)] text-[var(--child-1-primary)] ring-[var(--child-1-light)]",
  blue: "bg-[var(--child-2-light)] text-[var(--child-2-primary)] ring-[var(--child-2-light)]",
} as const;

const childRing = {
  orange:
    "ring-[var(--child-1-primary)] bg-[var(--child-1-light)] text-[var(--child-1-primary)]",
  blue: "ring-[var(--child-2-primary)] bg-[var(--child-2-light)] text-[var(--child-2-primary)]",
} as const;

export function BookPage() {
  const {
    appointmentRequests,
    upcomingAppointments,
    bookAppointmentRequest,
    rescheduleAppointment,
  } = useDemo();

  const [rescheduleId] = useState<string | null>(() => getRescheduleId());
  const rescheduling = rescheduleId
    ? upcomingAppointments.find((a) => a.id === rescheduleId)
    : null;
  const firstRequest = appointmentRequests[0];

  const [childId, setChildId] = useState<Child["id"]>(
    rescheduling?.childId ??
      firstRequest?.childId ??
      children[0]?.id ??
      "jeanne",
  );
  const [reasonId, setReasonId] = useState<string>(
    appointmentReasons[0]?.id ?? "controle",
  );
  const [slotKey, setSlotKey] = useState<string | null>(null);

  const confirm = () => {
    if (!slotKey) return;
    const parsed = parseSlot(slotKey);
    if (!parsed) return;
    if (rescheduling) {
      rescheduleAppointment(rescheduling.id, parsed.isoDate, parsed.time);
    } else if (firstRequest) {
      bookAppointmentRequest(firstRequest.id);
    }
    back();
  };

  return (
    <>
      <SectionTitle
        title={rescheduling ? "Déplacer le rendez-vous" : "Prendre rendez-vous"}
      />
      <PageShell>
        <Stack>
          <PatientPanel
            childId={childId}
            locked={Boolean(rescheduling || firstRequest)}
            onChange={setChildId}
          />

          <ReasonPanel
            reasonId={reasonId}
            onChange={setReasonId}
            lockedLabel={rescheduling?.kind ?? firstRequest?.reason ?? null}
          />

          <Card title="Créneaux disponibles">
            <ul className="space-y-3 pt-1">
              {availableSlots.map((day) => (
                <li key={day.id}>
                  <p className="mb-1.5 text-[12px] font-semibold uppercase tracking-wide text-[var(--text-2)]">
                    {day.weekday} {day.date}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {day.times.map((t) => {
                      const key = `${day.id}__${t}`;
                      const selected = slotKey === key;
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setSlotKey(key)}
                          className={`inline-flex h-10 items-center rounded-full px-4 text-[13px] font-semibold ring-1 transition ${selected ? "bg-[var(--sf-primary)] text-white ring-[var(--sf-primary)]" : "bg-[var(--surface-1)] text-[var(--text-2)] ring-[var(--border-1)] hover:bg-[var(--surface-2)]"}`}
                        >
                          {t}
                        </button>
                      );
                    })}
                  </div>
                </li>
              ))}
            </ul>
          </Card>

          <Card title="Récapitulatif">
            <SummaryLine
              label="Patient"
              value={children.find((c) => c.id === childId)?.firstName ?? "—"}
              pillTone={children.find((c) => c.id === childId)?.color}
            />
            <SummaryLine
              label="Motif"
              value={
                appointmentReasons.find((r) => r.id === reasonId)?.label ?? "—"
              }
            />
            <SummaryLine
              label="Créneau"
              value={(() => {
                if (!slotKey) return "À choisir";
                const p = parseSlot(slotKey);
                if (!p) return "À choisir";
                const date = formatFrenchDate(p.isoDate);
                return `${date.charAt(0).toUpperCase() + date.slice(1)} · ${p.time}`;
              })()}
            />
            <button
              type="button"
              disabled={!slotKey}
              onClick={confirm}
              className="mt-4 inline-flex h-12 w-full items-center justify-center rounded-full bg-[var(--sf-primary)] px-6 text-[15px] font-semibold text-white shadow-sm transition active:scale-[0.99] disabled:cursor-not-allowed disabled:bg-[var(--surface-3)] disabled:text-[var(--text-3)] sm:mx-auto sm:max-w-sm"
            >
              Confirmer le rendez-vous
            </button>
          </Card>
        </Stack>
      </PageShell>
    </>
  );
}

function SummaryLine({
  label,
  value,
  pillTone,
}: {
  label: string;
  value: string;
  pillTone?: keyof typeof childPill;
}) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-[var(--border-1)] py-2 last:border-b-0">
      <span className="text-[13px] font-medium text-[var(--text-2)]">
        {label}
      </span>
      {pillTone ? (
        <span
          className={`inline-flex h-6 items-center rounded-full px-3 text-[12px] font-semibold ring-1 ring-inset ${childPill[pillTone]}`}
        >
          {value}
        </span>
      ) : (
        <span className="text-[13.5px] font-semibold text-[var(--text-1)]">
          {value}
        </span>
      )}
    </div>
  );
}

function ReasonPanel({
  reasonId,
  onChange,
  lockedLabel,
}: {
  reasonId: string;
  onChange: (id: string) => void;
  lockedLabel: string | null;
}) {
  if (lockedLabel) {
    return (
      <Card title="Motif">
        <div className="flex items-center gap-3">
          <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-[var(--sf-primary-100)] text-[var(--sf-primary)]">
            <IconClipboard width={20} height={20} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[15px] font-semibold text-[var(--text-1)]">
              {lockedLabel}
            </p>
            <p className="text-[12px] text-[var(--text-3)]">
              Défini par le cabinet
            </p>
          </div>
        </div>
      </Card>
    );
  }
  return (
    <Card title="Motif">
      <ul className="space-y-2 pt-1">
        {appointmentReasons.map((r) => {
          const selected = reasonId === r.id;
          return (
            <li key={r.id}>
              <button
                type="button"
                onClick={() => onChange(r.id)}
                className={`flex w-full items-start gap-3 rounded-2xl border p-3 text-left transition ${selected ? "border-[var(--sf-primary)] bg-[var(--sf-primary-100)]" : "border-[var(--border-1)] bg-[var(--surface-1)] hover:bg-[var(--surface-2)]"}`}
              >
                <span
                  className={`mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full border-2 ${selected ? "border-[var(--sf-primary)] bg-[var(--sf-primary)]" : "border-[var(--border-2)]"}`}
                >
                  {selected ? (
                    <span className="size-1.5 rounded-full bg-[var(--surface-1)]" />
                  ) : null}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="flex items-baseline justify-between gap-2 text-[14.5px] font-semibold text-[var(--text-1)]">
                    <span className="truncate">{r.label}</span>
                    <span className="shrink-0 text-[11.5px] font-medium text-[var(--text-2)]">
                      {r.duration}
                    </span>
                  </p>
                  <p className="mt-0.5 text-[12.5px] text-[var(--text-2)]">
                    {r.description}
                  </p>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function PatientPanel({
  childId,
  locked,
  onChange,
}: {
  childId: Child["id"];
  locked: boolean;
  onChange: (id: Child["id"]) => void;
}) {
  if (locked) {
    const child = children.find((c) => c.id === childId);
    if (!child) return null;
    return (
      <Card title="Patient">
        <div className="flex items-center gap-3">
          <span
            className={`inline-flex size-10 shrink-0 items-center justify-center rounded-full text-[14px] font-bold ${childPill[child.color]}`}
          >
            {child.firstName.charAt(0)}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[15px] font-semibold text-[var(--text-1)]">
              {child.firstName}
            </p>
            <p className="text-[12px] text-[var(--text-3)]">
              Défini par le cabinet
            </p>
          </div>
        </div>
      </Card>
    );
  }
  return (
    <Card title="Pour qui ?">
      <ul className="flex flex-wrap gap-2 pt-1">
        {children.map((c) => {
          const selected = childId === c.id;
          return (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => onChange(c.id)}
                className={`inline-flex h-10 items-center rounded-full px-4 text-[14px] font-semibold ring-1 transition ${selected ? childRing[c.color] : "bg-[var(--surface-1)] text-[var(--text-2)] ring-[var(--border-1)] hover:bg-[var(--surface-2)]"}`}
              >
                {c.firstName}
              </button>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
